import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, LinearProgress } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { getNurse } from '../api/nurses';
import { Nurse } from '../types/nurse';
import { Patient } from '../types/patient';
import { DEVELOPER_THEME } from '../types/common';
// コンポーネントのインポート
import ProfileSection from '../components/nurse/ProfileSection';
import WorkshopSection from '../components/nurse/WorkshopSection';
import BlockedPatientsSection from '../components/nurse/BlockedPatientsSection';
import RelatedPatientsSection from '../components/nurse/RelatedPatientsSection';
import SupportNursesSection from '../components/nurse/SupportNursesSection';
import AssignedNursesSection from '../components/nurse/AssignedNursesSection';
import AdminTalkRoomsSection from '../components/nurse/AdminTalkRoomsSection';
import { DeveloperInfoSection } from '../components/common';

const NurseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ nurse, setNurse ] = useState<Nurse | null>(null);
  const [ relatedPatients, setRelatedPatients ] = useState<Patient[]>([]);
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  const [ loadingPatients, setLoadingPatients ] = useState(false);
  const [ patientsError, setPatientsError ] = useState<string | null>(null);
  const fetchNurseData = async() => {
    // idがない場合は何もしない
    if (!id) return;
    // ローディング状態を設定
    setLoading(true);
    setError(null);
    setLoadingPatients(true);
    setPatientsError(null);
    try {
      const nurseId = parseInt(id, 10);
      const data = await getNurse(nurseId);
      setNurse(data);
      // 関連患者データが含まれている場合は状態を更新
      if (data && data.related_patients && data.related_patients.length > 0) {
        setRelatedPatients(data.related_patients);
      } else {
        // 関連患者がない場合は空の配列を設定
        setRelatedPatients([]);
      }
    } catch (error) {
      console.error('看護師データの取得に失敗しました', error);
      setError('看護師データの取得に失敗しました。外部DBの接続設定を確認してください。');
      setNurse(null);
      setRelatedPatients([]);
    } finally {
      setLoading(false);
      setLoadingPatients(false);
    }
  };
  // 看護師データ取得のためのAPIデータ読み込み
  useEffect(() => {
    fetchNurseData();
  }, [ id ]); // idのみに依存
  const handleNurseUpdate = () => {
    // 看護師情報を再取得
    fetchNurseData();
  };
  const handleBack = () => {
    // 単純に看護師一覧に戻る（ローカルストレージに保存された状態が自動的に復元される）
    navigate('/nurses');
  };
  if (loading) {
    return <LinearProgress/>;
  }
  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon/>} sx={{ mt: 2 }} onClick={handleBack}>
          一覧に戻る
        </Button>
      </Box>
    );
  }
  if (!nurse) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="info">看護師データが見つかりません。</Alert>
        <Button startIcon={<ArrowBackIcon/>} sx={{ mt: 2 }} onClick={handleBack}>
          一覧に戻る
        </Button>
      </Box>
    );
  }
  // ロールタイプに基づいて表示する順序を定義
  const renderSectionsByRole = () => {
    // どのロールタイプにも共通する基本コンポーネント
    const commonSections = (
      <>
        {/* 基本プロフィール情報 */}
        <ProfileSection nurse={nurse} onNurseUpdate={handleNurseUpdate}/>

        <DeveloperInfoSection
          data={{
            'user_id': nurse.user_id,
            'tokens (for auth)': nurse.tokens ?? {},
            'fcm_tokens': nurse.fcm_tokens ?? {}
          }}
        />
      </>
    );
    // ロールタイプ別のセクション表示順序
    switch (nurse.role_type) {
      case 'STAFF': // 研修看護師
        return (
          <>
            {commonSections}
            <WorkshopSection nurse={nurse}/>
            <SupportNursesSection nurse={nurse}/>
            <RelatedPatientsSection
              nurse={nurse}
              relatedPatients={relatedPatients}
              loadingPatients={loadingPatients}
              patientsError={patientsError}
            />
          </>
        );
      case 'TALK_STAFF': // 対話看護師
        return (
          <>
            {commonSections}
            <RelatedPatientsSection
              nurse={nurse}
              relatedPatients={relatedPatients}
              loadingPatients={loadingPatients}
              patientsError={patientsError}
            />
            <BlockedPatientsSection nurse={nurse}/>
            <SupportNursesSection nurse={nurse}/>
            <WorkshopSection nurse={nurse}/>
          </>
        );
      case 'MANAGER': // マネージャー
        return (
          <>
            {commonSections}
            <RelatedPatientsSection
              nurse={nurse}
              relatedPatients={relatedPatients}
              loadingPatients={loadingPatients}
              patientsError={patientsError}
            />
            <BlockedPatientsSection nurse={nurse}/>
            <AdminTalkRoomsSection nurse={nurse}/>
            <AssignedNursesSection nurse={nurse}/>
            <SupportNursesSection nurse={nurse}/>
            <WorkshopSection nurse={nurse}/>
          </>
        );
      case 'OWNER': // オーナー
        return (
          <>
            {commonSections}
            <AssignedNursesSection nurse={nurse}/>
            <RelatedPatientsSection
              nurse={nurse}
              relatedPatients={relatedPatients}
              loadingPatients={loadingPatients}
              patientsError={patientsError}
            />
            <BlockedPatientsSection nurse={nurse}/>
            <AdminTalkRoomsSection nurse={nurse}/>
            <SupportNursesSection nurse={nurse}/>
            <WorkshopSection nurse={nurse}/>
          </>
        );
      default: // 基本的に存在しないが念の為に定義
        return (
          <>
            {commonSections}
            <SupportNursesSection nurse={nurse}/>
            <AssignedNursesSection nurse={nurse}/>
            <RelatedPatientsSection
              nurse={nurse}
              relatedPatients={relatedPatients}
              loadingPatients={loadingPatients}
              patientsError={patientsError}
            />
            <BlockedPatientsSection nurse={nurse}/>
            <AdminTalkRoomsSection nurse={nurse}/>
            <WorkshopSection nurse={nurse}/>
          </>
        );
    }
  };
  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon/>} onClick={handleBack}>
          一覧に戻る
        </Button>
      </Box>

      {/* テストアカウント警告バナー */}
      {nurse.is_test_account && (
        <Alert
          severity="warning"
          sx={{
            mb: 3,
            backgroundColor: DEVELOPER_THEME.BACKGROUND,
            border: `2px solid ${DEVELOPER_THEME.BORDER}`,
            borderRadius: 1,
            '& .MuiAlert-icon': { color: DEVELOPER_THEME.DARK },
            '& .MuiAlert-message': { fontWeight: 'bold', color: DEVELOPER_THEME.DARK }
          }}
        >
          🧪 このアカウントはテスト用データです
        </Alert>
      )}

      {renderSectionsByRole()}
    </Box>
  );
};
export default NurseDetail;
